import { useState } from 'react';
import type { LogoData } from '@bliks-io/contracts';
import {
  useConfirmOrgLogoUpload,
  useDeleteOrgLogo,
  useRequestOrgLogoUploadUrl
} from '@/api/mutations/useOrgLogoUpload';
import { uploadToPresignedUrl } from '@/lib/file-utils';

/** Display URL for a stored org logo (SVG as-is, raster prefers WebP medium). */
function deriveOrgLogoUrl(logo: LogoData | undefined | null): string {
  if (!logo) return '';
  return logo.type === 'svg' ? logo.svg || '' : logo.medium?.webp || logo.medium?.png || '';
}

function extractLogoErrorMessage(err: unknown): string | undefined {
  if (
    err &&
    typeof err === 'object' &&
    'error' in err &&
    err.error &&
    typeof err.error === 'object' &&
    'message' in err.error
  ) {
    return String(err.error.message);
  }
  return undefined;
}

/**
 * The org-logo edit flow behind both settings pages (the customer app's and the
 * partner area's): pending file / removal state derived against the server
 * logo, plus the presigned upload → confirm (or delete) save orchestration.
 *
 * `save()` resolves the pending change and resets the local state; on failure
 * it stores the API error message in `error` and rethrows so the caller can
 * show its own feedback.
 */
export function useOrgLogoEditor(serverLogoData: LogoData | undefined | null) {
  const requestLogoUrl = useRequestOrgLogoUploadUrl();
  const confirmLogoUpload = useConfirmOrgLogoUpload();
  const deleteLogo = useDeleteOrgLogo();

  const [file, setFile] = useState<File | null>(null);
  const [removed, setRemoved] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [isSaving, setIsSaving] = useState(false);

  const serverLogo = deriveOrgLogoUrl(serverLogoData);
  // Derived, not synced via effect: the displayed stored-logo URL follows the
  // server value unless the user removed it (a fresh upload previews via `file`).
  const logo = removed ? '' : serverLogo;

  const handleFileChange = (files: FileList | null) => {
    if (!files?.length) return;
    setFile(files[0]);
  };

  const handleRemove = () => {
    setFile(null);
    setRemoved(true);
  };

  const isDirty = file !== null || (removed && serverLogo !== '');

  const save = async (): Promise<void> => {
    setError(undefined);
    setIsSaving(true);
    try {
      if (file) {
        const presigned = await requestLogoUrl.mutateAsync();
        await uploadToPresignedUrl(presigned.url, presigned.fields, file);
        // Confirm triggers server-side processing and updates the organization
        // (the backend also deletes the previous logo).
        await confirmLogoUpload.mutateAsync({ key: presigned.key, mimeType: file.type });
      } else if (removed && serverLogo) {
        await deleteLogo.mutateAsync();
      }
      setFile(null);
      setRemoved(false);
    } catch (err: unknown) {
      setError(extractLogoErrorMessage(err));
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  return { file, logo, error, isDirty, isSaving, handleFileChange, handleRemove, save };
}
